import { useState, useEffect } from 'react'
import type { LendingPosition } from '../../types/lending'
import type { StageAllocation } from '../../types/strategy'
import { useAuth } from '../../auth/AuthContext'
import { useWalletBalances } from '../../hooks/useWalletBalance'
import { useWalletAdapterBridge } from '../../lib/walletBridge'
import TokenIcon from '../common/TokenIcon'
import SuggestionCard from '../shared/SuggestionCard'
import BorrowForm from '../trade/BorrowForm'
import CollateralForm from '../trade/CollateralForm'

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

function authHeader(): Record<string, string> {
  const token = sessionStorage.getItem('jwt')
  return token ? { Authorization: `Bearer ${token}` } : {}
}


interface BorrowPosition {
  vaultId: number
  positionId: number
  supplySymbol: string
  supplyAddress: string
  supplyAmount: number
  supplyUsd: number
  supplyApy: number
  borrowSymbol: string
  borrowAddress: string
  borrowAmount: number
  borrowUsd: number
  borrowApr: number
  link: string
}

interface Props {
  positions: LendingPosition[]
  onClose: () => void
  borrowStage?: StageAllocation
  lendStage?: StageAllocation
  initialSection?: 'collateral' | 'debt'
}

function usd(n: number) { return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) }
function compact(n: number) {
  if (n >= 1000) return '$' + (n / 1000).toFixed(2) + 'K'
  return usd(n)
}

export default function JupiterBorrowPanel({ onClose, borrowStage, lendStage, initialSection }: Props) {
  const { portfolioId } = useAuth()
  const balances = useWalletBalances(portfolioId)
  useWalletAdapterBridge()

  const [borrowPos, setBorrowPos] = useState<BorrowPosition | null>(null)
  const [loading, setLoading] = useState(true)
  const [section, setSection] = useState<'collateral' | 'debt'>(
    initialSection ?? (lendStage && !borrowStage ? 'collateral' : 'debt')
  )

  // Fetch portfolio positions to discover vaultId + positionId (nftId)
  useEffect(() => {
    if (!portfolioId) { setLoading(false); return }
    setLoading(true)
    fetch(`${BASE}/api/jupiter/lend/portfolio-positions?user=${encodeURIComponent(portfolioId)}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (!data?.elements) return
        const el = data.elements.find((e: any) => e.type === 'borrowlend' && e.data?.link?.includes('/lend/borrow/'))
        if (!el) return
        const d = el.data
        const link: string = d.link ?? ''
        const parts = link.match(/\/borrow\/(\d+)\/nfts\/(\d+)/)
        const vId = parts ? parseInt(parts[1]) : 0
        const pId = parts ? parseInt(parts[2]) : 0

        const supply = d.suppliedAssets?.[0]
        const borrow = d.borrowedAssets?.[0]
        const supplyYields = supply?.data?.yields?.[0]
        const borrowYields = d.borrowedYields?.[0]?.[0]

        setBorrowPos({
          vaultId: vId,
          positionId: pId,
          supplySymbol: data.tokenInfo?.solana?.[supply?.data?.address]?.symbol ?? supply?.data?.symbol ?? '?',
          supplyAddress: supply?.data?.address ?? '',
          supplyAmount: supply?.data?.amount ?? 0,
          supplyUsd: supply?.value ?? 0,
          supplyApy: supplyYields?.apy ?? 0,
          borrowSymbol: data.tokenInfo?.solana?.[borrow?.data?.address]?.symbol ?? borrow?.data?.symbol ?? '?',
          borrowAddress: borrow?.data?.address ?? '',
          borrowAmount: borrow?.data?.amount ?? 0,
          borrowUsd: borrow?.value ?? 0,
          borrowApr: Math.abs(borrowYields?.apr ?? 0),
          link,
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [portfolioId])

  // Build Jupiter URL — open exact position if exists, otherwise vault page
  const DEFAULT_VAULT_ID = 13
  const vaultId = borrowPos?.vaultId || DEFAULT_VAULT_ID
  const jupiterUrl = borrowPos && borrowPos.positionId > 0
    ? `https://jup.ag/lend/borrow/${vaultId}/nfts/${borrowPos.positionId}`
    : `https://jup.ag/lend/borrow/${vaultId}`

  const supplySymbol = borrowPos?.supplySymbol ?? 'JupSOL'
  const borrowSymbol = borrowPos?.borrowSymbol ?? 'USDC'

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative w-full max-w-lg bg-[#131a2a] border-l border-fringe-strong shadow-2xl flex flex-col h-full animate-slide-in-right overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-fringe-strong shrink-0">
          <div className="flex items-center gap-2">
            {borrowPos && (
              <TokenIcon symbol={supplySymbol} mint={borrowPos.supplyAddress} size={20} />
            )}
            <span className="text-sm font-bold text-ink">
              {supplySymbol}/{borrowSymbol}
            </span>
            {borrowPos && borrowPos.positionId > 0 && (
              <span className="text-[10px] text-gray-500">#{borrowPos.positionId}</span>
            )}
          </div>
          <button onClick={onClose} className="ml-auto text-gray-500 hover:text-ink transition-colors p-1">
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        {/* Position summary */}
        {borrowPos && (
          <div className="grid grid-cols-2 gap-2 px-4 py-2 border-b border-fringe-strong shrink-0">
            <div className="rounded-lg p-2 bg-accent-green/10 border border-accent-green/20">
              <div className="text-[9px] text-gray-500 mb-0.5">Supplied Collateral</div>
              <div className="flex items-center gap-1.5">
                <TokenIcon symbol={supplySymbol} mint={borrowPos.supplyAddress} size={16} />
                <span className="text-xs text-ink font-semibold">{borrowPos.supplyAmount.toFixed(2)} {supplySymbol}</span>
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-[9px] text-ink-muted">{usd(borrowPos.supplyUsd)}</span>
                {borrowPos.supplyApy > 0 && (
                  <span className="text-[9px] text-accent-green font-semibold">
                    {(borrowPos.supplyApy * 100).toFixed(1)}% APY
                  </span>
                )}
              </div>
            </div>
            <div className="rounded-lg p-2 bg-accent-blue/10 border border-accent-blue/20">
              <div className="text-[9px] text-gray-500 mb-0.5">Borrowed Debt</div>
              <div className="flex items-center gap-1.5">
                <TokenIcon symbol={borrowSymbol} mint={borrowPos.borrowAddress} size={16} />
                <span className="text-xs text-ink font-semibold">{compact(borrowPos.borrowAmount)} {borrowSymbol}</span>
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-[9px] text-ink-muted">{usd(borrowPos.borrowUsd)}</span>
                {borrowPos.borrowApr > 0 && (
                  <span className="text-[9px] text-red-400 font-semibold">
                    {(borrowPos.borrowApr * 100).toFixed(1)}% APR
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recommended info section */}
        {(borrowStage || lendStage) && (
          <div className="px-4 py-2 border-b border-fringe-strong shrink-0 space-y-1.5">
            {lendStage && lendStage.gapUsd != null && Math.abs(lendStage.gapUsd) > 1 && (() => {
              const isUnder = lendStage.gapUsd > 0
              const gapAbs = Math.abs(lendStage.gapUsd)
              const gapPct = lendStage.amountUsd > 0 ? gapAbs / lendStage.amountUsd : 0
              return (
                <SuggestionCard
                  action={isUnder ? 'Deposit more collateral' : 'Withdraw excess collateral'}
                  amount={`${isUnder ? '+' : '−'}${usd(gapAbs)}`}
                  pctChange={`(${(gapPct * 100).toFixed(1)}%)`}
                  isPositive={isUnder}
                  description={isUnder
                    ? 'Your lending position is below target. More deposited collateral increases borrowing capacity and earns additional yield APR.'
                    : 'You have deposited more than this strategy requires. Withdrawing the surplus frees capital for the reserve.'}
                  impacts={[
                    { label: 'Current', value: usd(lendStage.actualAmountUsd ?? 0) },
                    { label: 'Target', value: `${usd(lendStage.amountUsd)}${lendStage.primaryAssetAmount != null ? ` · ${lendStage.primaryAssetAmount.toFixed(4)} ${lendStage.primaryAssetSymbol ?? ''}` : ''}` },
                    ...(lendStage.yieldOrCostApr ? [{ label: 'Supply APY', value: (lendStage.yieldOrCostApr * 100).toFixed(1) + '%', color: 'text-accent-green' }] : []),
                  ]}
                />
              )
            })()}
            {borrowStage && borrowStage.gapUsd != null && Math.abs(borrowStage.gapUsd) > 1 && (() => {
              const isUnder = borrowStage.gapUsd > 0
              const gapAbs = Math.abs(borrowStage.gapUsd)
              const gapPct = borrowStage.amountUsd > 0 ? gapAbs / borrowStage.amountUsd : 0
              return (
                <SuggestionCard
                  action={isUnder ? 'Draw additional USDC' : 'Repay excess USDC'}
                  amount={`${isUnder ? '+' : '−'}${usd(gapAbs)}`}
                  pctChange={`(${(gapPct * 100).toFixed(1)}%)`}
                  isPositive={isUnder}
                  description={isUnder
                    ? 'Increasing the borrow deploys more capital into LP and Loop stages, amplifying overall yield while health factor remains above the safe threshold.'
                    : 'You have borrowed more than recommended. Repaying the excess reduces daily interest cost and lifts health factor, creating more buffer against liquidation.'}
                  impacts={[
                    { label: 'Current', value: usd(borrowStage.actualAmountUsd ?? 0) },
                    { label: 'Target', value: `${usd(borrowStage.amountUsd)}${borrowStage.primaryAssetAmount != null ? ` · ${borrowStage.primaryAssetAmount.toFixed(2)} ${borrowStage.primaryAssetSymbol ?? ''}` : ''}` },
                    ...(borrowStage.yieldOrCostApr ? [{ label: 'Borrow APR', value: (Math.abs(borrowStage.yieldOrCostApr) * 100).toFixed(1) + '%', color: 'text-red-400' }] : []),
                  ]}
                />
              )
            })()}
          </div>
        )}

        {/* Section toggle — collateral (jupSOL) vs debt (USDC) */}
        <div className="px-4 py-2 border-b border-fringe-strong shrink-0">
          <div className="flex bg-surface-hover rounded-lg p-0.5">
            {(['collateral', 'debt'] as const).map(s => {
              const active = section === s
              const activeClass = s === 'collateral'
                ? 'bg-accent-green/20 text-accent-green'
                : 'bg-accent-blue/20 text-accent-blue'
              return (
                <button
                  key={s}
                  onClick={() => setSection(s)}
                  className={`flex-1 py-1.5 rounded-md text-[11px] font-bold ${
                    active ? activeClass : 'text-gray-500 hover:text-ink-soft'
                  }`}
                >
                  {s === 'collateral' ? `Supply ${supplySymbol}` : `Borrow ${borrowSymbol}`}
                </button>
              )
            })}
          </div>
        </div>

        {/* Native form — signs via wallet adapter, no iframe. */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin" />
                <span className="text-[10px] text-gray-500">Loading position…</span>
              </div>
            </div>
          ) : (
            <>
              {section === 'collateral'
                ? <CollateralForm balances={balances} />
                : <BorrowForm balances={balances} />}
              <div className="mt-3 text-center text-[10px] text-gray-400">
                Having trouble signing? <a href={jupiterUrl} target="_blank" rel="noopener noreferrer" className="text-accent-purple hover:underline">Open on Jupiter ↗</a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
